/**
 * Copy content from copyText and update the state of the button
 * If copyText is HTMLElement the copied text will be rich-text.
 *
 * @param {HTMLButtonElement} buttonEl - The button element that was clicked to copy
 * @param {HTMLElement|String} copyText - Element to have its innerHTML harvested, or text supplied directly
 */
function copyHandler(buttonEl, copyText) {
  let clipboardOp;
  let type;

  if (typeof copyText === 'string') {
    type = 'string';
  } else if (copyText instanceof HTMLElement) {
    type = 'html';
  } else {
    // Only copy if type matches
    return;
  }

  if (type === 'html') {
    // ClipboardItem is more modern, might not be supported
    if (typeof ClipboardItem !== 'undefined') {
      // Use rich text handling for HTMLElement
      const blobContentHtml = new Blob([copyText.innerHTML], {
        type: 'text/html',
      });
      const blobContentPlain = new Blob([copyText.innerText], {
        type: 'text/plain',
      });
      const clipboardItem = new ClipboardItem({
        'text/html': blobContentHtml,
        'text/plain': blobContentPlain,
      });
      clipboardOp = navigator.clipboard.write([clipboardItem]);
    } else {
      // Wrap the fallback in a Promise so it can be processed the same way
      clipboardOp = new Promise((resolve, reject) => {
        try {
          const range = document.createRange();
          window.getSelection().empty();
          range.selectNode(copyText);
          window.getSelection().addRange(range);
          // Deprecated command for browsers not supporting ClipboardItem
          document.execCommand('copy');
          window.getSelection().empty();
          resolve();
        } catch (e) {
          reject();
        }
      });
    }
  } else {
    // Use writeText for String
    clipboardOp = navigator.clipboard.writeText(copyText);
  }

  // clipboardOp is always promise
  clipboardOp
    .then(() => {
      const cachedText = buttonEl.innerText;

      // Show new text and prevent clicking again (though not stricly neccessary?)
      buttonEl.innerText = 'Copied';
      buttonEl.disabled = true;

      // Allow new text to be displayed temporarily before swapping back
      setTimeout(() => {
        resetCopyButton(buttonEl, cachedText);
      }, 3000);
    })
    .catch(() => {
      // In case navigator.clipboard.write gives an error
      alert(
        "Your browser doesn't allow copying this way, please copy the text manually.",
      );
    });
}

/**
 * Reset a copy to clipboard button to its default state
 *
 * @param {HTMLButtonElement} buttonEl - The button to target
 * @param {String} text - What the contents of the button should say
 */
function resetCopyButton(buttonEl, text) {
  buttonEl.innerText = text;
  buttonEl.disabled = false;
}

export default document.addEventListener('DOMContentLoaded', () => {
  // Use event delegation to capture events on clipboard buttons dynamically
  document.body.addEventListener('click', (e) => {
    if (e.target.classList.contains('copy-to-clipboard-button')) {
      // Each button will need to have a sibling "copy-to-clipboard-node" or provide "data-copy-text"
      const copyText = e.target.dataset?.copyText
        ? e.target.dataset?.copyText
        : e.target.parentElement.querySelector('.copy-to-clipboard-node');
      copyHandler(e.target, copyText);
    }
  });
});
